import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { formatDateTime } from '../lib/utils';
import DOMPurify from 'dompurify';
import parseHtml from 'html-react-parser';
import {
  PaperPlaneTilt,
  Spinner,
  CheckCircle,
  Warning,
  Phone,
  ClockCounterClockwise,
  Eye,
  PencilSimple,
  EnvelopeSimple,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import api from '../services/api';

/**
 * /admin/odm-cert/support-email
 *
 * Single-page composer that loads the canonical CERT support draft from the
 * backend, lets the user edit subject/recipient/CC/body, previews the
 * rendered HTML, sends via Resend, and shows the audit history. Designed
 * specifically for unblocking ODM CERT Phase 6 — the user's first contact
 * with the ODM EDI Helpdesk to request CERT-environment Medicaid IDs +
 * NPI registration confirmation.
 */

const splitCsv = (s) =>
  (s || '')
    .split(',')
    .map((x) => x.trim())
    .filter(Boolean);

const RecipientInfoCard = ({ info }) => (
  <Card className="p-4 bg-slate-50 border-slate-200" data-testid="cert-email-recipient-info">
    <h3 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
      <EnvelopeSimple size={16} className="text-indigo-600" />
      ODM EDI Helpdesk
    </h3>
    <dl className="text-xs text-slate-600 space-y-1">
      <div className="flex">
        <dt className="w-24 text-slate-500">Primary:</dt>
        <dd className="font-mono">{info?.primary || '—'}</dd>
      </div>
      <div className="flex">
        <dt className="w-24 text-slate-500">Escalation:</dt>
        <dd className="font-mono">{info?.escalation || '—'}</dd>
      </div>
      <div className="flex">
        <dt className="w-24 text-slate-500"><Phone size={11} className="inline" /> Phone:</dt>
        <dd>{info?.phone || '—'}</dd>
      </div>
      <div className="flex">
        <dt className="w-24 text-slate-500">Hours:</dt>
        <dd>{info?.hours || '—'}</dd>
      </div>
    </dl>
    <p className="text-[10px] text-slate-400 mt-2">Source: {info?.source}</p>
  </Card>
);

const HistoryItem = ({ item }) => (
  <div className="border border-slate-200 rounded p-3 text-xs space-y-1" data-testid={`cert-email-history-${item.id}`}>
    <div className="flex items-center justify-between">
      <span className="font-mono text-slate-500">
        {formatDateTime(item.sent_at)}
      </span>
      {item.delivered ? (
        <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">
          <CheckCircle size={10} weight="fill" className="mr-1" />
          Delivered
        </Badge>
      ) : (
        <Badge className="bg-amber-100 text-amber-700 border-amber-200">
          <Warning size={10} weight="fill" className="mr-1" />
          Not delivered
        </Badge>
      )}
    </div>
    <div className="font-medium text-slate-900 truncate">{item.subject}</div>
    <div className="text-slate-500 truncate">
      <span className="text-slate-400">to </span>
      {item.to}
      {item.cc?.length ? <span className="text-slate-400"> · cc {item.cc.join(', ')}</span> : null}
    </div>
    {item.error && <div className="text-red-600 font-mono">{item.error}</div>}
    {item.message_id && (
      <div className="text-slate-400 font-mono text-[10px]">Resend id: {item.message_id}</div>
    )}
  </div>
);

export default function OdmCertSupportEmailPage() {
  const [draft, setDraft] = useState({ subject: '', body_md: '' });
  const [recipients, setRecipients] = useState(null);
  const [resendOk, setResendOk] = useState(true);
  const [to, setTo] = useState('');
  const [cc, setCc] = useState('');
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewing, setPreviewing] = useState(false);
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);
  const [activeTab, setActiveTab] = useState('compose');

  const loadDraft = useCallback(async () => {
    try {
      const res = await api.get('/odm-cert/support-email/draft');
      setDraft({ subject: res.data.subject, body_md: res.data.body_md });
      setRecipients(res.data.recipients);
      setResendOk(!!res.data.resend_configured);
      setTo(res.data.default_to || res.data.recipients?.primary || '');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load draft');
    }
  }, []);

  const loadHistory = useCallback(async () => {
    try {
      const res = await api.get('/odm-cert/support-email/history?limit=20');
      setHistory(res.data?.items || []);
    } catch (e) {
      // Non-blocking: history is supplementary, log for debugging
      console.warn('[OdmCertSupportEmail] history fetch failed', e);
    }
  }, []);

  useEffect(() => {
    loadDraft();
    loadHistory();
  }, [loadDraft, loadHistory]);

  const refreshPreview = useCallback(async () => {
    if (!draft.body_md) return;
    setPreviewing(true);
    try {
      const res = await api.post('/odm-cert/support-email/preview', {
        to: to || 'noop@example.com',
        subject: draft.subject || 'Preview',
        body_md: draft.body_md,
        cc: splitCsv(cc),
      });
      setPreviewHtml(res.data?.html || '');
    } catch (err) {
      toast.error('Preview failed');
    } finally {
      setPreviewing(false);
    }
  }, [draft.body_md, draft.subject, to, cc]);

  // Live preview on tab switch (saves the user a click)
  useEffect(() => {
    if (activeTab === 'preview') {
      refreshPreview();
    }
  }, [activeTab, refreshPreview]);

  const ccList = useMemo(() => splitCsv(cc), [cc]);

  // Defense-in-depth: even though the backend's md_to_html escapes all user
  // content via Python's html.escape() before applying inline markdown, we
  // also DOMPurify-sanitize the response on the client. Belt + suspenders.
  // Allow only the inline tags + structure we actually emit; strip every
  // event-handler attribute even if it somehow makes it through.
  // We then parse the sanitized HTML to a JSX tree with html-react-parser
  // rather than mounting via React's escape-hatch innerHTML prop — that
  // escape hatch is unnecessary once DOMPurify has done its job.
  const previewJsx = useMemo(
    () => parseHtml(
      DOMPurify.sanitize(previewHtml || '', {
        ALLOWED_TAGS: [
          'div', 'p', 'br', 'h1', 'h2', 'h3', 'h4',
          'ul', 'ol', 'li', 'strong', 'em', 'code', 'pre',
          'table', 'thead', 'tbody', 'tr', 'th', 'td',
          'span', 'a',
        ],
        ALLOWED_ATTR: ['style', 'href', 'target', 'rel'],
        // Drop anything dangerous regardless of allowlist
        FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'form'],
        FORBID_ATTR: [
          'onerror', 'onload', 'onclick', 'onmouseover',
          'onfocus', 'onblur', 'onsubmit', 'onchange',
        ],
      }),
    ),
    [previewHtml],
  );

  const handleSend = async () => {
    if (!to.trim()) {
      toast.error('Recipient is required');
      return;
    }
    if (!draft.subject.trim() || !draft.body_md.trim()) {
      toast.error('Subject and body are required');
      return;
    }
    if (!window.confirm(`Send this email to ${to}${ccList.length ? ` (cc ${ccList.length})` : ''}?\n\nThis goes directly to ODM and is auditable.`)) {
      return;
    }
    setSending(true);
    try {
      const res = await api.post('/odm-cert/support-email/send', {
        to,
        subject: draft.subject,
        body_md: draft.body_md,
        cc: ccList,
      });
      toast.success(`Email sent (Resend id: ${res.data?.message_id || 'queued'})`);
      loadHistory();
      setActiveTab('history');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Send failed');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-4 max-w-6xl" data-testid="cert-support-email-page">
      <header>
        <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
          <EnvelopeSimple size={32} className="text-indigo-700" weight="duotone" />
          Email ODM CERT Support
        </h1>
        <p className="text-sm text-slate-600 mt-1">
          One-click message to the Ohio Department of Medicaid EDI Helpdesk to request
          CERT-environment NPI registration + test recipient Medicaid IDs.
        </p>
      </header>

      {!resendOk && (
        <Card className="p-3 bg-amber-50 border-amber-300 flex items-start gap-2" data-testid="resend-not-configured">
          <Warning size={18} className="text-amber-700 shrink-0 mt-0.5" />
          <div className="text-xs text-amber-900">
            Resend API key is not configured. The body and audit row will save when you hit Send,
            but no email will actually be delivered until <span className="font-mono">RESEND_API_KEY</span> is set.
            Copy/paste the rendered body manually to your mail client in the meantime.
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-4">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="compose" data-testid="tab-compose">
              <PencilSimple size={14} className="mr-1.5" /> Compose
            </TabsTrigger>
            <TabsTrigger value="preview" data-testid="tab-preview">
              <Eye size={14} className="mr-1.5" /> Preview
            </TabsTrigger>
            <TabsTrigger value="history" data-testid="tab-history">
              <ClockCounterClockwise size={14} className="mr-1.5" /> History ({history.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="compose">
            <Card className="p-5 space-y-4">
              <div>
                <Label htmlFor="cert-email-to">To</Label>
                <Input
                  id="cert-email-to"
                  type="email"
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                  className="font-mono"
                  data-testid="cert-email-to"
                />
              </div>
              <div>
                <Label htmlFor="cert-email-cc">CC <span className="text-xs text-slate-400">(comma-separated)</span></Label>
                <Input
                  id="cert-email-cc"
                  value={cc}
                  onChange={(e) => setCc(e.target.value)}
                  placeholder="you@yourcompany.com, escalation@example.com"
                  className="font-mono"
                  data-testid="cert-email-cc"
                />
              </div>
              <div>
                <Label htmlFor="cert-email-subject">Subject</Label>
                <Input
                  id="cert-email-subject"
                  value={draft.subject}
                  onChange={(e) => setDraft({ ...draft, subject: e.target.value })}
                  data-testid="cert-email-subject"
                />
              </div>
              <div>
                <Label htmlFor="cert-email-body">Body (markdown — bold, lists, tables, code all supported)</Label>
                <Textarea
                  id="cert-email-body"
                  value={draft.body_md}
                  onChange={(e) => setDraft({ ...draft, body_md: e.target.value })}
                  rows={20}
                  className="font-mono text-xs"
                  data-testid="cert-email-body"
                />
              </div>
              <div className="flex justify-between items-center">
                <Button variant="outline" size="sm" onClick={() => setActiveTab('preview')}>
                  <Eye size={14} className="mr-2" /> Preview
                </Button>
                <Button
                  onClick={handleSend}
                  disabled={sending}
                  className="bg-indigo-600 hover:bg-indigo-700"
                  data-testid="cert-email-send"
                >
                  {sending ? (
                    <><Spinner size={14} className="animate-spin mr-2" /> Sending…</>
                  ) : (
                    <><PaperPlaneTilt size={14} className="mr-2" /> Send to ODM</>
                  )}
                </Button>
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="preview">
            <Card className="p-5 space-y-3">
              <div className="text-xs text-slate-500 border-b pb-2 space-y-0.5">
                <div><span className="text-slate-400">To: </span><span className="font-mono">{to || '—'}</span></div>
                {ccList.length > 0 && (
                  <div><span className="text-slate-400">Cc: </span><span className="font-mono">{ccList.join(', ')}</span></div>
                )}
                <div><span className="text-slate-400">Subject: </span><span className="font-medium">{draft.subject}</span></div>
              </div>
              {previewing ? (
                <div className="flex items-center gap-2 text-slate-500"><Spinner size={14} className="animate-spin" /> Rendering…</div>
              ) : (
                <div
                  className="prose prose-sm max-w-none border border-slate-200 rounded p-4 bg-white"
                  data-testid="cert-email-preview-html"
                >
                  {previewJsx}
                </div>
              )}
            </Card>
          </TabsContent>

          <TabsContent value="history">
            <Card className="p-5 space-y-3" data-testid="cert-email-history">
              {history.length === 0 ? (
                <p className="text-sm text-slate-500">No emails sent yet.</p>
              ) : (
                history.map((it) => <HistoryItem key={it.id} item={it} />)
              )}
            </Card>
          </TabsContent>
        </Tabs>

        <div className="space-y-4">
          <RecipientInfoCard info={recipients} />
          <Card className="p-4 bg-indigo-50 border-indigo-200 text-xs text-indigo-900">
            <p className="font-semibold mb-2 flex items-center gap-1.5">
              <CheckCircle size={14} weight="duotone" /> What we're asking ODM
            </p>
            <ul className="space-y-1.5 list-disc list-inside">
              <li>Confirm our submitter NPI <span className="font-mono">1124627245</span> is registered in CERT</li>
              <li>Provide CERT-environment test Medicaid Recipient IDs (12-digit format)</li>
              <li>Confirm <span className="font-mono">NM1*41 NM109</span> should carry our Trading Partner ID <span className="font-mono">0022168</span> (per CG v19.0)</li>
            </ul>
          </Card>
        </div>
      </div>
    </div>
  );
}
